<script>
  import { createEventDispatcher } from 'svelte';
  import { auth } from '../../firebase-config.js';
  import { signInWithPopup, signInWithRedirect, signInWithCredential, GoogleAuthProvider } from 'firebase/auth';
  import { isNativeApp } from '../../platform.js';
  import { guestMode } from '../store.js';

  export let cancellable = true;
  export let allowGuest = false;

  const dispatch = createEventDispatcher();
  let loading = false;
  let error = '';

  async function loginWithGoogle() {
    let redirectTriggered = false;
    try {
      loading = true;
      error = '';
      if (isNativeApp) {
        // Google blocks OAuth popups inside WebViews (disallowed_useragent) —
        // the native app must use the platform Google Sign-In flow and hand
        // the resulting ID token to the Firebase JS SDK.
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager: false });
        const idToken = result?.credential?.idToken;
        if (!idToken) throw new Error('Google sign-in was cancelled.');
        const credential = GoogleAuthProvider.credential(idToken, result?.credential?.accessToken);
        await signInWithCredential(auth, credential);
      } else {
        const provider = new GoogleAuthProvider();
        // signInWithRedirect breaks on mobile browsers/PWAs when the app origin
        // (ffpl-scorer.pages.dev) differs from Firebase's authDomain
        // (…firebaseapp.com): after the redirect back, the OAuth credential lives
        // in authDomain storage that the browser's third-party storage
        // partitioning blocks us from reading, so getRedirectResult never
        // resolves and the login modal reappears in a loop. Popup hands the
        // credential back via postMessage (no cross-domain storage read), so
        // prefer it everywhere and only fall back to redirect where a popup
        // can't open (iOS standalone PWA, popup blockers).
        try {
          await signInWithPopup(auth, provider);
        } catch (err) {
          if (
            err.code === 'auth/popup-blocked' ||
            err.code === 'auth/operation-not-supported-in-this-environment' ||
            err.code === 'auth/cancelled-popup-request'
          ) {
            redirectTriggered = true;
            await signInWithRedirect(auth, provider);
            return; // Page will redirect; getRedirectResult (main.js) completes it.
          }
          throw err;
        }
      }
      guestMode.set(false);
      dispatch('close');
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        error = 'Login popup was closed. Try again.';
      } else if (err.code === 'auth/popup-blocked') {
        error = 'Popup blocked by browser. Allow popups for this site and try again.';
      } else {
        error = err.message || 'Google login failed.';
      }
      console.error(err);
    } finally {
      if (!redirectTriggered) {
        loading = false;
      }
    }
  }

  function continueAsGuest() {
    guestMode.set(true);
    dispatch('close');
  }

  function handleBackdropClick(event) {
    if (cancellable && event.target === event.currentTarget) dispatch('close');
  }

  function handleModalKeydown(event) {
    if (cancellable && event.key === 'Escape') dispatch('close');
  }
</script>

<svelte:window on:keydown={handleModalKeydown} />

<div class="modal-backdrop" role="presentation" on:click={handleBackdropClick}>
  <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="login-modal-title" tabindex="-1">
    <div class="modal-header" style="display:flex; align-items:center; gap:10px;">
      <img src="/criccam_pro_logo.png" alt="CricCam Pro Logo" style="width:32px; height:32px; border-radius:8px; object-fit:cover;" />
      <h2 id="login-modal-title" style="margin:0;">Login to CricCam Pro</h2>
      {#if cancellable}
        <button class="close-btn" style="margin-left:auto;" on:click={() => dispatch('close')}>✕</button>
      {/if}
    </div>

    <div class="modal-body">
      <div class="modal-note">
        Use your Google account to sync matches and manage your player profile securely.
      </div>

      {#if error}
        <div class="error-msg">{error}</div>
      {/if}

      <button class="google-btn" on:click={loginWithGoogle} disabled={loading}>
        <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        {loading ? 'Connecting...' : 'Continue with Google'}
      </button>

      {#if allowGuest}
        <button class="guest-btn" on:click={continueAsGuest} disabled={loading}>
          Continue as Guest
        </button>
        <div class="guest-note">Guest mode scores matches on this device only — login later anytime to sync to the cloud.</div>
      {/if}
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  }
  .modal-content {
    background: var(--surface, #0f172a);
    border: 1px solid var(--border, #334155);
    color: white;
    width: 90%;
    max-width: 360px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
    overflow: hidden;
  }
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-soft, #1e293b);
  }
  .modal-header h2 {
    margin: 0;
    font-size: 1.1rem;
  }
  .close-btn {
    background: none;
    border: none;
    color: #999;
    font-size: 1.5rem;
    cursor: pointer;
  }
  .modal-body {
    padding: 26px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .modal-note {
    text-align: center;
    color: var(--muted, #94a3b8);
    font-size: 0.9rem;
  }
  .google-btn {
    background: white;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 48px;
    padding: 13px;
    border-radius: 10px;
    border: none;
    font-size: 1.05rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;
  }
  .google-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .guest-btn {
    min-height: 46px;
    border: 1px solid var(--border, #334155);
    border-radius: 10px;
    background: transparent;
    color: var(--text, #e5e7eb);
    font-size: 0.95rem;
    font-weight: 600;
  }
  .guest-btn:active { background: var(--surface-2, #1e293b); }
  .guest-note {
    text-align: center;
    color: var(--muted-2, #64748b);
    font-size: 0.72rem;
    line-height: 1.4;
  }
  .error-msg {
    background: rgba(239, 68, 68, 0.12);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #fca5a5;
    padding: 10px;
    border-radius: 8px;
    font-size: 0.9rem;
  }
</style>
